import React, {Component} from "react"

interface FocalPoint {
    x: number
    y: number,
    z: number
}

interface FocalImageProps {
    src: string
    alt?: string
    focalPoint: FocalPoint
}

const DEFAULT_IMAGE_STYLES = {
    position: "absolute",
    left: 0,
    top: 0,
    //transition: "all 0.1s ease-in-out", //slider is more responsive without this IMO 
}

const CONTAINER_STYLES: any = {
    position: "relative",
    height: "100%",
    width: "100%",
    overflow: "hidden",
}

export class FocalImage extends Component<FocalImageProps> {
    img: HTMLImageElement | null | undefined
    container: HTMLDivElement | null | undefined
    animationFrame?: number

    shouldComponentUpdate(nextProps: Readonly<FocalImageProps>, nextState: Readonly<{}>, nextContext: any): boolean {
        return nextProps.src !== this.props.src
            || nextProps.focalPoint.x !== this.props.focalPoint.x
            || nextProps.focalPoint.y !== this.props.focalPoint.y
            || nextProps.focalPoint.z !== this.props.focalPoint.z
    }

    componentDidMount() {
        this.reRender()
        window.addEventListener("resize", this.reRender)
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.reRender)
    }

    reRender = () => {
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
        this.animationFrame = requestAnimationFrame(() => this.forceUpdate())
    }


    calculateBoxBasedOnFocalPointRel(height: number, width: number, fx: number, fy: number, fz: number = 1) {
        fx = Math.max(0, Math.min(fx, 1));
        fy = Math.max(0, Math.min(fy, 1));
        return this.calculateBoxBasedOnFocalPoint({Height: height, Width: width}, {
            X: fx * width,
            Y: fy * height,
            Z: fz
        })
    }

    //logic from Berlitz20.ContentLoader.ServiceLogic\Images\Rectangle.cs
    //preserved for easier debugging
    calculateBoxBasedOnFocalPoint(original: { Height: number, Width: number }, focalPoint: { X: number, Y: number, Z: number }, widthRatio = 16, heightRatio = 9) {
        const originalTaller = original.Height / original.Width >= heightRatio / widthRatio;
        let width = originalTaller ? original.Width : original.Height * (widthRatio / heightRatio);
        let height = originalTaller ? original.Width * (heightRatio / widthRatio) : original.Height;

        //AG zoom in width and height
        width = Math.round(width / focalPoint.Z);
        height = Math.round(height / focalPoint.Z);

        const fxMin = focalPoint.X - width / 2;
        const fyMin = focalPoint.Y - height / 2;
        const fxMax = focalPoint.X + width / 2;
        const fyMax = focalPoint.Y + height / 2;
        let x = fxMin <= 0 ? 0 : fxMax <= original.Width ? fxMin : fxMin - (fxMax - original.Width);
        let y = fyMin <= 0 ? 0 : fyMax <= original.Height ? fyMin : fyMin - (fyMax - original.Height);

        //AG added to avoid Bugs! below
        x = Math.round(x);
        y = Math.round(y);

        if (x < 0 || y < 0 || x + width > original.Width || y + height > original.Height || Math.abs(width / height - widthRatio / heightRatio) > 0.01) {
            console.error("Bugs!")
        }

        //console.log(`H:${height} W:${width} X:${x} Y:${y} fz:${focalPoint.Z}`)
        return {
            height,
            width,
            x,
            y
        }
    }

    getImageStyles() {
        if (this.img && this.container) {
            const {focalPoint} = this.props
            const imageHeight = this.img.naturalHeight
            const imageWidth = this.img.naturalWidth
            const crop = this.calculateBoxBasedOnFocalPointRel(imageHeight, imageWidth, focalPoint.x, focalPoint.y, focalPoint.z)

            const containerWidth = this.container.getBoundingClientRect().width

            if (!imageHeight || !imageWidth || !containerWidth) {
                return {display: "none"}
            }

            const style: any = {
                ...DEFAULT_IMAGE_STYLES,
            }

            const scale = containerWidth / crop.width;
            style.left = -crop.x * scale;
            style.top = -crop.y * scale;
            style.width = imageWidth * scale;

            return style
        }
    }

    render() {
        const {src, alt} = this.props
        return (
            <div ref={el => (this.container = el)} style={CONTAINER_STYLES}>
                <img
                    ref={el => (this.img = el)}
                    style={this.getImageStyles()}
                    src={src}
                    alt={alt}
                    onLoad={this.reRender}
                />
            </div>
        )
    }
}
